import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { AppRoutes } from '@/constants';
import { NavRoute } from '@/types';
import { mergeClasses } from '@/utils';

import { SvgIcon } from '@/components/common';

import { NAV_ITEMS } from './FloatingNavBar.constants';
import { INavItem } from './FloatingNavBar.interfaces';

const FloatingNavBar = () => {
  const [hoveredItem, setHoveredItem] = useState<INavItem | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  const onClick = (item: NavRoute) => {
    navigate(item);
  };

  const isItemActive = (itemId: NavRoute): boolean => {
    if (itemId === AppRoutes.HOME) {
      return location.pathname === AppRoutes.HOME;
    }

    if (itemId === AppRoutes.FACE_ID_PROFILES) {
      return (
        location.pathname === AppRoutes.HOME ||
        location.pathname.startsWith(AppRoutes.FACE_ID_PROFILES)
      );
    }
    return location.pathname.startsWith(itemId);
  };

  return (
    <nav className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-[200]">
      <div className="bg-brand-primary-dark rounded-softer p-2 flex items-center gap-2">
        {NAV_ITEMS.map((item: INavItem) => {
          const isActive = isItemActive(item.id);
          const isHovered = hoveredItem?.id === item.id;
          const showText = isActive || isHovered;

          return (
            <button
              key={item.id}
              onClick={() => onClick(item.id)}
              onMouseEnter={() => setHoveredItem(item)}
              onMouseLeave={() => setHoveredItem(null)}
              className={mergeClasses(
                // Base classes
                'flex items-center transition-all duration-500 ease-in-out overflow-hidden backdrop-blur-[20px] p-2 border rounded-soft',
                // State-based classes
                isActive && 'bg-white border-opacity-light-20',
                showText && !isActive && 'bg-opacity-light-30 border-white',
                !showText && !isActive && 'bg-opacity-light-20 border-opacity-light-10',
              )}
            >
              <span
                className={mergeClasses(
                  // Base classes
                  'flex items-center justify-center flex-shrink-0 transition-colors duration-500 ease-in-out',
                  // Active state logic
                  isActive ? 'text-brand-primary-dark' : 'text-white',
                )}
              >
                <SvgIcon component={item.icon} />
              </span>
              <span
                className={mergeClasses(
                  // Base classes
                  'text-body-sm font-semibold whitespace-nowrap overflow-hidden transition-all duration-500 ease-in-out',
                  // Expansion logic
                  showText ? 'max-w-[300px] opacity-100 ml-2' : 'max-w-0 opacity-0 ml-0',
                  // Active state logic
                  isActive ? 'text-brand-primary-dark' : 'text-white',
                )}
              >
                {item.label}
              </span>
            </button>
          );
        })}
      </div>
    </nav>
  );
};

export default FloatingNavBar;
